import { IconBoil } from "@tine/designsystem-icons/sharp";
import type { CategoryInfo } from "../../common/types/categoryTypes";
import TagLink from "../TagLink";

import { cn } from "@tine/designsystem-utils";

import { Select } from "@tine/designsystem-ui-react";
import { useLocation } from "wouter";
import { GA_NAVIGATION_MAIN_CATEGORY, GA_NAVIGATION_SUB_CATEGORY, sendInternalLinkClick } from "../../common/tracking";
import productStore from "../../stores/product/productStore";
import Level2CategoryNavigation from "./Level2CategoryNavigation";

type CategoryNavigationProps = {
   currentLevel1Category?: CategoryInfo;
   currentLevel2Category?: CategoryInfo;
   rootCategory: CategoryInfo;
   onClick?: (e: React.MouseEvent<HTMLElement>) => void;
};

export const CategoryNavigation = ({ currentLevel1Category, currentLevel2Category, rootCategory }: CategoryNavigationProps) => {
   const [_, navigate] = useLocation();
   const hasLevel2 = !!currentLevel1Category?.children?.length;
   const borderOverStyle = "tw-border-t tw-pt-t tw-border-t-ink-border-subtle tw-my-2";
   const level1ClassName = cn("tw-flex tw-flex-row tw-flex-wrap tw-gap-2 tw-my-2");
   const narrowClassName = cn("tw-block md:tw-hidden");
   const wideClassName = cn("tw-hidden md:tw-block");
   const totalAssortmentProducts = (skus: string[]) => productStore.resolveSkus(skus, true).length;

   const className = "tw-text-h4 tw-no-underline hover:tw-underline";
   const level1CategoriesContainingProducts = rootCategory.children.filter((category) => totalAssortmentProducts(category.skus));
   const level2CategoriesContainingProducts =
      currentLevel1Category?.children.filter((category) => totalAssortmentProducts(category.skus)) ?? [];

   const level1Options = [
      { label: "Alle", value: "", id: "Alle" },
      ...level1CategoriesContainingProducts.map((category) => ({
         label: category.name,
         value: category.id,
         id: category.id
      }))
   ];
   const level2Options = [
      { label: "Alle", value: "" },
      ...level2CategoriesContainingProducts.map((category) => ({
         label: category.name,
         value: category.id
      }))
   ];
   const level2CurrentOption = level2Options.find((option) => option.value === currentLevel2Category?.id) ?? level2Options[0];
   const level1CurrentOption = level1Options.find((option) => option.value === currentLevel1Category?.id) ?? level1Options[0];

   const level2SelectChangeHandler = (option: { label: string; value: string }) => {
      const selectedCategory = currentLevel1Category?.children.find((category) => category.id === option.value);
      if (selectedCategory) {
         sendInternalLinkClick(selectedCategory?.fullUrl, GA_NAVIGATION_SUB_CATEGORY, selectedCategory.name);
         navigate(selectedCategory.fullUrl);
      } else if (currentLevel1Category) {
         sendInternalLinkClick(currentLevel1Category?.fullUrl, GA_NAVIGATION_MAIN_CATEGORY, currentLevel1Category.name);
         navigate(currentLevel1Category.fullUrl);
      } else {
         sendInternalLinkClick(rootCategory.fullUrl, GA_NAVIGATION_MAIN_CATEGORY, rootCategory.name);
         navigate(rootCategory.fullUrl);
      }
   };

   const level1SelectChangeHandler = (option: { label: string; value: string }) => {
      const selectedCategory = rootCategory.children.find((category) => category.id === option.value);
      if (selectedCategory) {
         sendInternalLinkClick(selectedCategory.fullUrl, GA_NAVIGATION_MAIN_CATEGORY, selectedCategory.name);
         navigate(selectedCategory.fullUrl);
      } else {
         sendInternalLinkClick(rootCategory.fullUrl, GA_NAVIGATION_MAIN_CATEGORY, rootCategory.name);
         navigate(rootCategory.fullUrl);
      }
   };
   return (
      <>
         <div className={narrowClassName}>
            {!!level1CurrentOption && (
               <Select
                  label="Velg kategori"
                  options={level1Options}
                  value={level1CurrentOption}
                  onChange={level1SelectChangeHandler}
               />
            )}
            {hasLevel2 && !!level2CurrentOption && (
               <Select
                  label="Velg underkategori"
                  options={level2Options}
                  value={level2CurrentOption}
                  onChange={level2SelectChangeHandler}
                  className="tw-mt-3"
               />
            )}
         </div>
         <div className={wideClassName}>
            <div className={level1ClassName}>
               <TagLink
                  url={rootCategory.fullUrl}
                  isActive={!currentLevel1Category}
                  className={className}
                  trackingLocationName={GA_NAVIGATION_MAIN_CATEGORY}
                  label="Alle produkter"
               />

               {level1CategoriesContainingProducts.map((category) => {
                  const label =
                     category.url_key === "nyheter" ? (
                        <div className="tw-flex tw-flex-row">
                           <IconBoil />
                           {category.name}
                        </div>
                     ) : (
                        category.name
                     );
                  const className = cn("tw-text-h4 tw-no-underline hover:tw-underline");
                  return (
                     <div key={category.id}>
                        <TagLink
                           url={category.fullUrl}
                           isActive={category.id === currentLevel1Category?.id}
                           className={className}
                           trackingLocationName={GA_NAVIGATION_MAIN_CATEGORY}
                           label={label}
                        />
                     </div>
                  );
               })}
            </div>
            {hasLevel2 && (
               <div className={borderOverStyle}>
                  <Level2CategoryNavigation
                     currentLevel1Category={currentLevel1Category}
                     currentLevel2Category={currentLevel2Category}
                  />
               </div>
            )}
         </div>
      </>
   );
};
